import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from 'react-query';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Navigation, MapPin, Clock } from 'lucide-react';
import { serviceCallsAPI } from '@shared/apiClient';
import { getDirectionsUrl } from './DetailSection';
import 'leaflet/dist/leaflet.css';

function numberedIcon(number, isPriority) {
  const bg = isPriority ? '#dc2626' : '#2563eb';
  return L.divIcon({
    className: 'route-stop-icon',
    html: `<div style="background:${bg};color:white;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);">${number}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });
}

const shopIcon = L.divIcon({
  className: 'route-shop-icon',
  html: '<div style="background:#0f172a;color:white;border-radius:8px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:15px;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.4);">🏠</div>',
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

function FitBounds({ points }) {
  const map = useMap();
  const prevKeyRef = useRef('');
  const key = points.map((p) => `${p.lat},${p.lng}`).join('|');

  useEffect(() => {
    if (points.length === 0 || key === prevKeyRef.current) return;
    prevKeyRef.current = key;
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [key, points, map]);

  return null;
}

function formatMinutes(min) {
  if (min == null) return null;
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

const MS_CENTER = [32.35, -89.4];

export default function RouteMap({ date }) {
  const { data, isLoading, isFetching, error } = useQuery(
    ['todayRoute', date],
    () => serviceCallsAPI.getDailyRoute(date),
    { staleTime: 60000 }
  );

  const route = data?.data;
  const origin = route?.origin || null;
  const stops = route?.stops || [];
  const geometry = route?.geometry || null;

  const routableStops = useMemo(
    () => stops.filter((s) => s.lat != null && s.lng != null),
    [stops]
  );
  const unroutableStops = useMemo(
    () => stops.filter((s) => s.lat == null || s.lng == null),
    [stops]
  );

  const mapPoints = useMemo(() => {
    const pts = [];
    if (origin) pts.push({ lat: origin.lat, lng: origin.lng });
    routableStops.forEach((s) => pts.push({ lat: s.lat, lng: s.lng }));
    return pts;
  }, [origin, routableStops]);

  const polylinePositions = useMemo(() => {
    if (geometry?.coordinates?.length) {
      return geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    }
    if (mapPoints.length >= 2) {
      return mapPoints.map((p) => [p.lat, p.lng]);
    }
    return [];
  }, [geometry, mapPoints]);

  const center = mapPoints.length > 0 ? [mapPoints[0].lat, mapPoints[0].lng] : MS_CENTER;

  if (isLoading) {
    return <p className="text-sm text-slate-400">Building your route…</p>;
  }

  if (error) {
    return <p className="text-sm text-red-500">Couldn't load your route. Try refreshing.</p>;
  }

  if (routableStops.length === 0) {
    return (
      <div className="card p-6 text-center text-sm text-slate-500">
        {stops.length === 0
          ? 'No calls scheduled today to route.'
          : "None of today's calls have a known location yet, so a route can't be built."}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card overflow-hidden relative" style={{ height: 'calc(100vh - 380px)', minHeight: 360 }}>
        {isFetching && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] rounded-full bg-white/90 border border-slate-200 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-md backdrop-blur-sm">
            Updating route…
          </div>
        )}
        <MapContainer
          center={center}
          zoom={9}
          className="h-full w-full"
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={mapPoints} />
          {polylinePositions.length >= 2 && (
            <Polyline
              positions={polylinePositions}
              pathOptions={{
                color: '#2563eb',
                weight: 4,
                opacity: 0.75,
                dashArray: geometry ? null : '6 8',
              }}
            />
          )}
          {origin && (
            <Marker position={[origin.lat, origin.lng]} icon={shopIcon}>
              <Popup>
                <p className="font-semibold text-sm">Shop</p>
                {origin.address && <p className="text-xs text-gray-600">{origin.address}</p>}
              </Popup>
            </Marker>
          )}
          {routableStops.map((s) => (
            <Marker key={s.serviceCallId} position={[s.lat, s.lng]} icon={numberedIcon(s.order, s.isPriority)}>
              <Popup>
                <div className="min-w-[180px]">
                  <p className="font-bold text-sm text-gray-900 mb-1">{s.order}. {s.name}</p>
                  {s.address && <p className="text-xs text-gray-600 mb-2">{s.address}</p>}
                  {s.legMinutes != null && (
                    <p className="text-xs text-gray-500 mb-2">{formatMinutes(s.legMinutes)} from previous stop</p>
                  )}
                  {(() => {
                    const dirUrl = getDirectionsUrl({ latitude: s.lat, longitude: s.lng, address: s.address });
                    return dirUrl ? (
                      <a
                        href={dirUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-600 no-underline"
                      >
                        <Navigation className="h-3 w-3" />
                        Directions
                      </a>
                    ) : null;
                  })()}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <div className="card divide-y divide-slate-100">
        {origin && (
          <div className="flex items-center gap-3 px-4 py-3">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-900 text-sm text-white">🏠</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">Start at shop</p>
              {origin.address && <p className="text-xs text-slate-500">{origin.address}</p>}
            </div>
          </div>
        )}
        {routableStops.map((s) => (
          <div key={s.serviceCallId} className="flex items-center gap-3 px-4 py-3">
            <span
              className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${
                s.isPriority ? 'bg-red-600' : 'bg-blue-600'
              }`}
            >
              {s.order}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">{s.name}</p>
              {s.address && <p className="truncate text-xs text-slate-500">{s.address}</p>}
            </div>
            {s.legMinutes != null && (
              <div className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-slate-500">
                <Clock className="h-3.5 w-3.5" />
                {formatMinutes(s.legMinutes)}
              </div>
            )}
          </div>
        ))}
      </div>

      {unroutableStops.length > 0 && (
        <div className="card p-4">
          <p className="mb-2 flex items-center gap-1 text-xs font-semibold text-slate-500">
            <MapPin className="h-3.5 w-3.5" />
            Location unknown — not included in the route
          </p>
          <ul className="space-y-1">
            {unroutableStops.map((s) => (
              <li key={s.serviceCallId} className="text-sm text-slate-600">
                {s.name}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
